/*******************************************************************************
 * Copyright     : Copyright (c)  2021 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc., and its use or disclosure in whole or in
 *                 part without express written permission of Honeywell Inc.
 *                 is prohibited.
 *
 * Name          : Home.tsx
 *
 * Project Title : APM Settings Tool
 *
 * Description   : Preview Page
 *
 *
 * Company       : Honeywell Technology Solutions , Hyderabad
 *
 * @Author       : H422335
 *
 * @Date         : January 25th, 2022
 *
 * @Version      : Version 1.0
 *
 *
 ******************************************************************************/

import React, { FC } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Grid, Card, Divider, Icon, Button, Loader } from "@scuf/common";
import { SaveAPMFileModal } from "components/SaveAPMFileModal";
import { AccountModal } from "components/AccountModal";
import {
  EnumApmSettingsWorkFlow,
  EnumCurrentApmFileRecordStatus,
} from "models/configuration";
import { selectShowAccountModal } from "stores/UIStore";
import { selectGeneralInfoFormData } from "stores/GeneralInfoStore";
import { selectSelectedFileName } from "stores/SelectApmFileStore";
import {
  selectSelectedApmRecordLoader,
  selectCurrentApmFileRecord,
  setCurrentWorkFlowStep,
  setCurrentWorkFlowType,
} from "stores/ApmWorkFlowStore";
import { selectSelectedAircraftDetails } from "stores/AircraftDetailsStore";
import {
  setSaveFilePopUpModal,
  selectSaveDetailsLoading,
  selectSaveFileModalOpen,
  selectUpdatedAPMSettingsData,
} from "stores/ApmSettingsStore";
import "./PreviewApmSettings.scss";

export const PreviewApmSettings: FC = () => {
  const generalInfo = useSelector(selectGeneralInfoFormData);
  const selectedApmFile = useSelector(selectSelectedFileName);
  const selectedAircraftDetails = useSelector(selectSelectedAircraftDetails);
  const apmSettings = useSelector(selectUpdatedAPMSettingsData);
  const selectedSaveFileModalOpen = useSelector(selectSaveFileModalOpen);
  const saveAPMDetailsLoader = useSelector(selectSaveDetailsLoading);
  const selectedApmRecordLoader = useSelector(selectSelectedApmRecordLoader);
  const currentApmFileRecord = useSelector(selectCurrentApmFileRecord);
  const showAccountModal = useSelector(selectShowAccountModal);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const onEditSection = (step: number) => {
    if (
      currentApmFileRecord?.status === EnumCurrentApmFileRecordStatus.completed
    ) {
      navigate("/");
    } else {
      navigate("/new");
      dispatch(setCurrentWorkFlowType(EnumApmSettingsWorkFlow.Edit));
      dispatch(setCurrentWorkFlowStep(step));
    }
  };
  const onClickGenerateApmFile = () => {
    dispatch(setSaveFilePopUpModal(true));
  };

  return (
    <>
      <Grid className="preview-page-container">
        <Grid.Row>
          <Grid.Column className="preview-page-title">
            APM Settings Preview
          </Grid.Column>
        </Grid.Row>
      </Grid>
      {saveAPMDetailsLoader || selectedApmRecordLoader ? (
        <Loader minHeight={100} overlayOpacity={0.5}>
          <div className="placeholder" />
        </Loader>
      ) : (
        <Grid>
          <Grid.Row>
            <Grid.Column className="preview-page-section">
              <Card>
                <Card.Header
                  title="General Information"
                  className="section-header"
                >
                  {currentApmFileRecord?.status ===
                  EnumCurrentApmFileRecordStatus.inProgress ? (
                    <Button
                      type="link"
                      icon={<Icon name="edit" root="common" color="#707070" />}
                      content="Edit"
                      iconPosition="left"
                      onClick={() => onEditSection(1)}
                    />
                  ) : null}
                </Card.Header>
                <Divider />
                <Card.Content>
                  <Grid>
                    <Grid.Row>
                      <Grid.Column className="preview-field-label" lWidth={4}>
                        Customer/Company Name
                      </Grid.Column>
                      <Grid.Column className="preview-field-value" lWidth={4}>
                        {generalInfo.customerName}
                      </Grid.Column>
                      <Grid.Column lWidth={4}></Grid.Column>
                    </Grid.Row>
                    <Grid.Row>
                      <Grid.Column className="preview-field-label" lWidth={4}>
                        Customer Email Address
                      </Grid.Column>
                      <Grid.Column className="preview-field-value" lWidth={4}>
                        {generalInfo.customerEmail}
                      </Grid.Column>
                      <Grid.Column lWidth={4}></Grid.Column>
                    </Grid.Row>
                    <Grid.Row>
                      <Grid.Column className="preview-field-label" lWidth={4}>
                        Customer Phone Number
                      </Grid.Column>
                      <Grid.Column className="preview-field-value" lWidth={4}>
                        {generalInfo.customerPhone}
                      </Grid.Column>
                      <Grid.Column lWidth={4}></Grid.Column>
                    </Grid.Row>
                  </Grid>
                </Card.Content>
              </Card>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column className="preview-page-section">
              <Card>
                <Card.Header title="APM File" className="section-header">
                  {currentApmFileRecord?.status ===
                  EnumCurrentApmFileRecordStatus.inProgress ? (
                    <Button
                      type="link"
                      icon={<Icon name="edit" root="common" color="#707070" />}
                      content="Edit"
                      iconPosition="left"
                      onClick={() => onEditSection(2)}
                    />
                  ) : null}
                </Card.Header>
                <Divider />
                <Card.Content>
                  <Grid.Row>
                    <Grid.Column className="preview-field-label" lWidth={4}>
                      Existing APM File Selected
                    </Grid.Column>
                    <Grid.Column className="preview-field-value" lWidth={4}>
                      {selectedApmFile ? selectedApmFile.partNumber : "None"}
                    </Grid.Column>
                    <Grid.Column lWidth={4}></Grid.Column>
                  </Grid.Row>
                </Card.Content>
              </Card>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column className="preview-page-section">
              <Card>
                <Card.Header
                  title="Aircraft Details"
                  className="section-header"
                >
                  {currentApmFileRecord?.status ===
                  EnumCurrentApmFileRecordStatus.inProgress ? (
                    <Button
                      type="link"
                      icon={<Icon name="edit" root="common" color="#707070" />}
                      content="Edit"
                      iconPosition="left"
                      onClick={() => onEditSection(3)}
                    />
                  ) : null}
                </Card.Header>
                <Divider />
                <Card.Content>
                  <Grid>
                    <Grid.Row>
                      <Grid.Column lWidth={4} className="preview-field-label">
                        OEM Aircraft Make
                      </Grid.Column>
                      <Grid.Column lWidth={2} className="preview-field-value">
                        {selectedAircraftDetails?.make}
                      </Grid.Column>
                      <Grid.Column lWidth={4} className="preview-field-label">
                        OEM Aircraft Model
                      </Grid.Column>
                      <Grid.Column lWidth={2} className="preview-field-value">
                        {selectedAircraftDetails?.model}
                      </Grid.Column>
                    </Grid.Row>
                    <Grid.Row>
                      <Grid.Column lWidth={4} className="preview-field-label">
                        Aircraft Tail Number
                      </Grid.Column>
                      <Grid.Column lWidth={2} className="preview-field-value">
                        {selectedAircraftDetails?.tailNumber}
                      </Grid.Column>
                      <Grid.Column lWidth={4} className="preview-field-label">
                        Aircraft Serial Number
                      </Grid.Column>
                      <Grid.Column lWidth={2} className="preview-field-value">
                        {selectedAircraftDetails?.serialNumber}
                      </Grid.Column>
                    </Grid.Row>
                  </Grid>
                </Card.Content>
              </Card>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column className="preview-page-section">
              <Card>
                <Card.Header
                  title="APM Settings Details"
                  className="section-header"
                >
                  {currentApmFileRecord?.status ===
                  EnumCurrentApmFileRecordStatus.inProgress ? (
                    <Button
                      type="link"
                      icon={<Icon name="edit" root="common" color="#707070" />}
                      content="Edit"
                      iconPosition="left"
                      onClick={() => onEditSection(4)}
                    />
                  ) : null}
                </Card.Header>
                <Divider />
                <Card.Content className="settings-content">
                  {apmSettings?.map((apmSettingField) => (
                    <Grid.Row key={apmSettingField.id}>
                      <Grid.Column lWidth={8} className="preview-field-label">
                        {apmSettingField.name}
                      </Grid.Column>
                      <Grid.Column lWidth={4} className="preview-field-value">
                        {apmSettingField.value}
                      </Grid.Column>
                    </Grid.Row>
                  ))}
                </Card.Content>
              </Card>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row className="preview-footer">
            <Grid.Column lWidth={6} xsWidth={12}>
              {currentApmFileRecord?.status ===
              EnumCurrentApmFileRecordStatus.inProgress ? (
                <Button
                  type="secondary"
                  size="medium"
                  content="Back"
                  disabled={saveAPMDetailsLoader}
                  onClick={() => {
                    onEditSection(4);
                  }}
                />
              ) : null}
            </Grid.Column>
            <Grid.Column lWidth={6} xsWidth={12} className="confirm-button">
              {currentApmFileRecord?.status ===
              EnumCurrentApmFileRecordStatus.inProgress ? (
                <Button
                  actionType="submit"
                  type="primary"
                  size="medium"
                  content="Generate APM File"
                  disabled={saveAPMDetailsLoader}
                  onClick={onClickGenerateApmFile}
                />
              ) : (
                <Button
                  actionType="submit"
                  type="primary"
                  size="medium"
                  content="Back to My APM History"
                  onClick={() => navigate("/")}
                />
              )}
            </Grid.Column>
          </Grid.Row>
        </Grid>
      )}
      {selectedSaveFileModalOpen && <SaveAPMFileModal />}
      {showAccountModal && <AccountModal />}
    </>
  );
};
